import gql from 'graphql-tag';
import getGraphQLClient from '../../../src/GraphQL/graphQLClient';

export type SiteRecruitingStudyDetails = {
  nctId: string;
  title: string;
  description: string;
  phase: string;
  detailsUrl: string;
  eligibilityUrl: string;
};

type StudySearchResult = {
  name?: string;
  trialTitle?: { value?: string };
  trialDescription?: { value?: string };
  phase?: { value?: string };
  deactivateTrial?: { value?: string };
  relatedTaEprLink?: {
    jsonValue?: { href?: string; value?: { href?: string } } | null;
  };
};

type StudySearchResponse = {
  search?: {
    results?: StudySearchResult[];
  };
};

const isDeactivated = (value?: string): boolean => value === '1' || value?.toLowerCase() === 'true';

/**
 * One search: name is NCT1 OR NCT2 OR …
 * NCT count varies, so the OR clause is built from the list.
 */
const buildStudiesByNctQuery = (nctIds: string[]) => {
  const nameFilters = nctIds
    .map((_id, index) => `{ name: "_name", value: $name${index}, operator: EQ }`)
    .join('\n');
  const varDefs = nctIds.map((_id, index) => `$name${index}: String!`).join(', ');
  return gql`
    query GetSiteRecruitingStudies(
    $rootItemId: String!
    $language: String!
    $templateA: String!
    $templateB: String!
    ${varDefs}
    ) {
      search(
        where: {
          AND: [
            { name: "_path", value: $rootItemId, operator: CONTAINS }
            { 
              OR: [
              { name: "_templates", value: $templateA }
              { name: "_templates", value: $templateB }
              ] 
            }
            { name: "_language", value: $language }
            { 
              OR: [
                ${nameFilters}
              ]
            }
        ]
      }
      first: ${nctIds.length}
      ) {
        results {
          name
          trialTitle: field(name: "trialTitle") {
            value
          }
          trialDescription: field(name: "trialDescription") {
            value
          }
          phase: field(name: "phase") {
            value
          }
          deactivateTrial: field(name: "deactivateTrial") {
            value
          }
          relatedTaEprLink: field(name: "relatedTaEprLink") {
            jsonValue
          }
        }
      }
    }
  `;
};

export const getSiteRecruitingStudiesByNctIds = async (
  nctIds: string[],
  locale: string,
  studyFolderId: string
): Promise<SiteRecruitingStudyDetails[]> => {
  const ids = [...new Set(nctIds.filter(Boolean))];
  if (!ids.length || !studyFolderId) {
    return [];
  }

  const graphQLClient = getGraphQLClient();
  const query = buildStudiesByNctQuery(ids);

  const variables: Record<string, string> = {
    rootItemId: studyFolderId,
    language: locale,
    templateA: '{B8F591BE-F27F-405F-A020-A1195453E669}',
    templateB: '{2C59A7F2-F9FA-4486-A26B-EE88CE85C62B}',
  };

  ids.forEach((nctId, index) => {
    variables[`name${index}`] = nctId;
  });

  const response = await graphQLClient.request<StudySearchResponse>(query as any, variables);

  const byNct = new Map<string, SiteRecruitingStudyDetails>();

  (response?.search?.results ?? []).forEach((item) => {
    const nctId = item.name;
    if (!nctId || isDeactivated(item.deactivateTrial?.value)) {
      return;
    }

    byNct.set(nctId, {
      nctId,
      title: item.trialTitle?.value || '',
      description: item.trialDescription?.value || '',
      phase: item.phase?.value || '',
      detailsUrl: `/find-trials/Study/${nctId}`,
      // General Link from Edge: jsonValue.value.href (not jsonValue.href)
      eligibilityUrl:
        item.relatedTaEprLink?.jsonValue?.value?.href ||
        item.relatedTaEprLink?.jsonValue?.href ||
        '',
    });
  });

  return ids
    .map((id) => byNct.get(id))
    .filter((study): study is SiteRecruitingStudyDetails => Boolean(study));
};
